import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Settings, Cookie } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const CookieBanner = () => {
  const { t } = useTranslation();
  const [showBanner, setShowBanner] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [preferences, setPreferences] = useState({
    essential: true,
    analytics: false,
    marketing: false,
    functional: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem('cookieConsent');
    if (!consent) {
      setShowBanner(true);
    } else {
      // Load saved preferences
      const saved = JSON.parse(consent);
      setPreferences(saved);
      applyConsent(saved);
    }

    // Listen for cookie settings event
    const handleOpenSettings = () => {
      setShowSettings(true);
      setShowBanner(false);
    };
    
    window.addEventListener('openCookieSettings', handleOpenSettings);
    
    return () => {
      window.removeEventListener('openCookieSettings', handleOpenSettings);
    };
  }, []);

  const applyConsent = (prefs) => {
    // Google Consent Mode v2
    if (window.gtag) {
      window.gtag('consent', 'update', {
        'analytics_storage': prefs.analytics ? 'granted' : 'denied',
        'ad_storage': prefs.marketing ? 'granted' : 'denied',
        'ad_user_data': prefs.marketing ? 'granted' : 'denied',
        'ad_personalization': prefs.marketing ? 'granted' : 'denied',
        'functionality_storage': prefs.functional ? 'granted' : 'denied',
        'personalization_storage': prefs.functional ? 'granted' : 'denied'
      });
    }

    // Store consent
    localStorage.setItem('cookieConsent', JSON.stringify(prefs));
    localStorage.setItem('cookieConsentDate', new Date().toISOString());
  };

  const handleAcceptAll = () => {
    const allAccepted = {
      essential: true,
      analytics: true,
      marketing: true,
      functional: true
    };
    setPreferences(allAccepted);
    applyConsent(allAccepted);
    setShowBanner(false);
    setShowSettings(false);
    
    // Reload to apply scripts
    setTimeout(() => window.location.reload(), 500);
  };

  const handleRejectAll = () => {
    const onlyEssential = {
      essential: true,
      analytics: false,
      marketing: false,
      functional: false
    };
    setPreferences(onlyEssential);
    applyConsent(onlyEssential);
    setShowBanner(false);
    setShowSettings(false);
  };

  const handleSavePreferences = () => {
    applyConsent(preferences);
    setShowBanner(false);
    setShowSettings(false);
    
    // Reload to apply scripts
    setTimeout(() => window.location.reload(), 500);
  };

  const togglePreference = (key) => {
    if (key === 'essential') return; // Can't disable essential
    setPreferences(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const cookieCategories = [
    {
      key: 'essential',
      name: t('cookie.settingsModal.categories.essential.name'),
      description: t('cookie.settingsModal.categories.essential.description'),
      required: true
    },
    {
      key: 'analytics',
      name: t('cookie.settingsModal.categories.analytics.name'),
      description: t('cookie.settingsModal.categories.analytics.description'),
      required: false
    },
    {
      key: 'marketing',
      name: t('cookie.settingsModal.categories.marketing.name'),
      description: t('cookie.settingsModal.categories.marketing.description'),
      required: false
    },
    {
      key: 'functional',
      name: t('cookie.settingsModal.categories.functional.name'),
      description: t('cookie.settingsModal.categories.functional.description'),
      required: false
    }
  ];

  return (
    <>
      {/* Cookie Banner */}
      <AnimatePresence>
        {showBanner && !showSettings && (
          <motion.div
            initial={{ y: 100, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            exit={{ y: 100, opacity: 0 }}
            className="fixed bottom-0 left-0 right-0 w-full z-50 p-4 md:p-6 bg-gradient-to-r from-gray-900 via-gray-800 to-gray-900 border-t border-gray-700 shadow-2xl"
            style={{ insetInlineStart: 0, insetInlineEnd: 0 }}
          >
            <div className="max-w-7xl mx-auto">
              <div className="flex flex-col md:flex-row items-start md:items-center gap-4">
                <Cookie className="w-8 h-8 text-blue-400 flex-shrink-0" />
                
                <div className="flex-1">
                  <h3 className="text-lg font-bold text-white mb-2 text-left rtl:text-right">
                    {t('cookie.title')}
                  </h3>
                  <p className="text-sm text-gray-300 text-left rtl:text-right">
                    {t('cookie.description')}
                  </p>
                  <a href="/cookie-policy" className="text-sm text-blue-400 hover:text-blue-300 underline mt-2 inline-block text-left rtl:text-right">
                    {t('cookie.moreDetails')}
                  </a>
                </div>

                <div className="flex flex-col sm:flex-row gap-3 w-full md:w-auto flex-shrink-0">
                  <button
                    onClick={() => setShowSettings(true)}
                    className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors flex items-center justify-center gap-2"
                  >
                    <Settings className="w-4 h-4" />
                    {t('cookie.settings')}
                  </button>
                  <button
                    onClick={handleRejectAll}
                    className="px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                  >
                    {t('cookie.decline')}
                  </button>
                  <button
                    onClick={handleAcceptAll}
                    className="px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:shadow-lg hover:shadow-blue-500/50 transition-all font-semibold"
                  >
                    {t('cookie.accept')}
                  </button>
                </div>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Cookie Settings Modal */}
      <AnimatePresence>
        {showSettings && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/70 backdrop-blur-sm"
            onClick={() => setShowSettings(false)}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              onClick={(e) => e.stopPropagation()}
              className="bg-gray-900 rounded-2xl shadow-2xl max-w-3xl w-full max-h-[90vh] overflow-hidden border border-gray-700"
            >
              {/* Header */}
              <div className="p-6 border-b border-gray-700 flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-white mb-1 text-left rtl:text-right">
                    {t('cookie.settingsModal.title')}
                  </h2>
                  <p className="text-sm text-gray-400 text-left rtl:text-right">
                    {t('cookie.settingsModal.subtitle')}
                  </p>
                </div>
                <button
                  onClick={() => setShowSettings(false)}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {/* Content */}
              <div className="p-6 overflow-y-auto max-h-[60vh]">
                <div className="space-y-6">
                  {cookieCategories.map((category) => (
                    <div
                      key={category.key}
                      className="bg-gray-800/50 rounded-xl p-5 border border-gray-700"
                    >
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <h3 className="text-lg font-semibold text-white mb-1 text-left rtl:text-right">
                            {category.name}
                          </h3>
                          <p className="text-sm text-gray-300 text-left rtl:text-right">{category.description}</p>
                        </div>
                        <div className="flex-shrink-0">
                          <label className="relative inline-flex items-center cursor-pointer">
                            <input
                              type="checkbox"
                              checked={preferences[category.key]}
                              onChange={() => togglePreference(category.key)}
                              disabled={category.required}
                              className="sr-only peer"
                            />
                            <div className={`w-14 h-7 rounded-full transition-all ${
                              preferences[category.key]
                                ? 'bg-gradient-to-r from-blue-500 to-cyan-500'
                                : 'bg-gray-600'
                            } ${category.required ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}>
                              <div className={`absolute top-0.5 left-0.5 bg-white w-6 h-6 rounded-full transition-transform ${
                                preferences[category.key] ? 'translate-x-7' : 'translate-x-0'
                              }`}></div>
                            </div>
                          </label>
                        </div>
                      </div>
                      {category.required && (
                        <p className="text-xs text-gray-500 mt-2 text-left rtl:text-right">
                          {t('cookie.settingsModal.alwaysEnabled')}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Footer */}
              <div className="p-6 border-t border-gray-700 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={handleSavePreferences}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-blue-500 to-cyan-500 text-white rounded-lg hover:shadow-lg hover:shadow-blue-500/50 transition-all font-semibold"
                >
                  {t('cookie.settingsModal.saveSettings')}
                </button>
                <button
                  onClick={handleAcceptAll}
                  className="flex-1 px-6 py-3 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors"
                >
                  {t('cookie.settingsModal.acceptAll')}
                </button>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CookieBanner;
